/**
 * Загрузка media с авторизацией.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО (замер, а не предположение, 2026-09-09 20:45 UTC):
 * наш Synapse отдаёт media ТОЛЬКО по авторизованным эндпоинтам.
 *
 *   GET https://matrix1.algsuccess.com/_matrix/media/v3/download/…   -> 404 M_NOT_FOUND
 *   GET https://matrix1.algsuccess.com/_matrix/client/v1/media/download/… -> 401
 *
 * То есть старый неавторизованный путь на сервере просто ОТКЛЮЧЁН (Matrix 1.11+,
 * authenticated media). Тег `<img src="…">` Bearer-токен отправить не умеет, поэтому
 * прямая ссылка в разметке даёт битую картинку — ровно это и было видно на
 * скриншоте 39 первого прогона: сообщение отправилось, а превью не загрузилось.
 *
 * АВАТАРЫ ТЕПЕРЬ ТОЖЕ ЗДЕСЬ (Антон, 2026-09-13: «аватары по-прежнему не
 * отображаются ни в списке, ни под сообщением»). Раньше `avatarOf` строил
 * прямую ссылку без авторизации, получал 401 и UI молча откатывался на
 * инициалы — выглядело как «аватаров нет», хотя на самом деле «картинку не
 * пустили». Теперь слои данных отдают mxc://-адрес, а картинку тянет
 * `useAuthedAvatar` — с токеном и через общий кэш (см. ниже).
 *
 * КАК РЕШЕНО: качаем через fetch с заголовком Authorization и отдаём
 * blob:-URL. Blob обязательно освобождается при размонтировании — иначе
 * пролистывание ленты с картинками течёт памятью до перезагрузки вкладки.
 */
import { useEffect, useState } from "react";
import { tf, tr } from "../i18n";
import type { MatrixClient } from "matrix-js-sdk";

/**
 * Скачать media как Blob с авторизацией. Общее ядро для хуков ниже:
 * и превью в ленте, и лайтбокс качают одинаково — разница только в том,
 * кому нужен сам Blob (лайтбоксу — для «Скачать»/«Поделиться»).
 */
/**
 * Ошибка HTTP при загрузке media: текст прежний, плюс код ответа — по нему
 * скачивание (src/downloads/attachmentDownload.ts) выбирает понятный тост
 * (401/403 — сессия, 404 — файл удалён, остальное — общий).
 */
export class MediaHttpError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function fetchAuthedBlob(client: MatrixClient, mxcUrl: string): Promise<Blob> {
  // useAuthentication=true -> /_matrix/client/v1/media/download/...
  const httpUrl = client.mxcUrlToHttp(mxcUrl, undefined, undefined, undefined, false, true, true);
  if (!httpUrl) throw new Error(tr("ui.ne_udalos_postroit_ssylku_iz_mxc"));
  const res = await fetch(httpUrl, {
    headers: { Authorization: `Bearer ${client.getAccessToken() ?? ""}` },
  });
  if (!res.ok) throw new MediaHttpError(tf("matrix.errorAttachmentServer", { status: res.status }), res.status);
  return await res.blob();
}

/**
 * Экспорт для уведомлений: иконка браузерного Notification — это URL,
 * и авторизованный media-сервер требует blob:-URL, как и вложения в ленте.
 * Вызывающий код обязан сам освободить objectURL (URL.revokeObjectURL).
 */
export async function fetchAuthedBlobUrl(
  client: MatrixClient,
  mxcUrl: string,
): Promise<string | null> {
  try {
    const blob = await fetchAuthedBlob(client, mxcUrl);
    return URL.createObjectURL(blob);
  } catch {
    // Нет аватара — не причина терять само уведомление: покажем без иконки.
    return null;
  }
}

/**
 * blob:-URL СЕРВЕРНОГО превью (/_matrix/.../thumbnail?width&height&method=crop).
 *
 * ЗАЧЕМ ОТДЕЛЬНО от useAuthedMedia: сетка «Мои медиа» в кабинете (задача
 * 2026-09-12 17:54) показывает десятки/сотни картинок разом. Качать
 * оригинал на каждый квадрат — трафик и память на ровном месте (Антон:
 * «сотни файлов, страница ляжет»). Сервер умеет отдавать уменьшенную
 * копию через /thumbnail — её и берём. Клик по квадрату уже тянет
 * оригинал через useAuthedMediaBlob (лайтбокс).
 *
 * Если сервер превью не сделал (например, это не картинка), запрос
 * вернёт ошибку — хук честно отдаёт error, UI рисует заглушку-иконку.
 */
export function useAuthedThumb(
  client: MatrixClient,
  mxcUrl: string | null,
  size = 320,
): { url: string | null; error: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mxcUrl) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        // width/height/method заданы -> mxcUrlToHttp строит thumbnail-URL,
        // а не download. useAuthentication=true -> авторизованный
        // /_matrix/client/v1/media/thumbnail (наш Synapse без Bearer 401).
        const httpUrl = client.mxcUrlToHttp(
          mxcUrl, size, size, "crop", false, true, true,
        );
        if (!httpUrl) throw new Error(tr("ui.ne_udalos_postroit_ssylku_iz_mxc"));
        const res = await fetch(httpUrl, {
          headers: { Authorization: `Bearer ${client.getAccessToken() ?? ""}` },
        });
        if (!res.ok) throw new Error(tf("matrix.errorAttachmentServer", { status: res.status }));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, mxcUrl, size]);

  return { url, error };
}

/**
 * blob:-URL для mxc-ссылки, или null пока грузится / если не получилось.
 * Второе значение — текст ошибки, чтобы UI мог сказать правду, а не показать
 * вечный «загружаю».
 */
export function useAuthedMedia(
  client: MatrixClient,
  mxcUrl: string | null,
): { url: string | null; error: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mxcUrl) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const blob = await fetchAuthedBlob(client, mxcUrl);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      // Освобождаем ВСЕГДА: иначе каждая пролистанная картинка остаётся в памяти.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, mxcUrl]);

  return { url, error };
}

/* ------------------------------------------------------------------------
 * АВАТАРЫ: общий кэш на всё приложение
 * --------------------------------------------------------------------- */

/**
 * ПОЧЕМУ АВАТАРЫ НЕ МОГУТ ИСПОЛЬЗОВАТЬ useAuthedMedia НАПРЯМУЮ.
 *
 * useAuthedMedia освобождает blob:-URL при размонтировании — для вложений
 * это правильно (картинка крупная и встречается один раз). Аватар же одного
 * человека повторяется десятками раз в ленте и живёт в списке комнат;
 * без кэша каждая прокрутка заново тянула бы те же файлы с сервера.
 *
 * Поэтому здесь модульный кэш mxc -> blob:-URL, общий на все компоненты,
 * и URL из него НЕ освобождается: аватаров немного (по числу людей и комнат),
 * они мелкие, и их повторное создание обошлось бы дороже удержания.
 * Потолок на случай очень большого сервера всё равно есть.
 */
const AVATAR_CACHE_LIMIT = 300;

/* ---- РАЗМЕР ЗАПРАШИВАЕМОГО THUMBNAIL (Антон 2026-09-18 12:21, п.9) ----
 *
 * «Аватары в рельсе спейсов размытые/низкого качества: запрашивать ≥2×
 * отображаемого размера с учётом devicePixelRatio, method=crop, и не
 * апскейлить маленький thumbnail CSS-ом.»
 *
 * Формула: displaySize × devicePixelRatio × 2, округлённая вверх до шага в
 * 32px. Шаг нужен, чтобы кэш (и кэш Synapse) не плодил десяток почти
 * одинаковых вариантов при дробном DPR (1.25, 1.5, 1.75 у Windows-масштаба).
 * Потолок 512: выше для кружка бессмысленно и только греет трафик.
 */
const AVATAR_THUMB_STEP = 32;
const AVATAR_THUMB_MAX = 512;

export function avatarThumbPx(displaySize: number): number {
  const dpr =
    typeof window !== "undefined" && Number.isFinite(window.devicePixelRatio) && window.devicePixelRatio > 0
      ? window.devicePixelRatio
      : 1;
  const wanted = displaySize * dpr * 2;
  const stepped = Math.ceil(wanted / AVATAR_THUMB_STEP) * AVATAR_THUMB_STEP;
  return Math.min(AVATAR_THUMB_MAX, Math.max(AVATAR_THUMB_STEP, stepped));
}

/**
 * Смена devicePixelRatio на лету: окно перетащили на другой монитор или
 * изменили масштаб страницы. Без подписки аватар, запрошенный для 1×,
 * остался бы размытым на Retina до перезагрузки — ровно тот симптом, что на
 * скрине Антона. matchMedia по resolution — единственный способ узнать об этом
 * событием, а не опросом.
 */
function useDevicePixelRatio(): number {
  const [dpr, setDpr] = useState(() =>
    typeof window !== "undefined" && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    let mql: MediaQueryList | null = null;
    let cancelled = false;
    const subscribe = () => {
      if (cancelled) return;
      const current = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
      setDpr(current);
      mql?.removeEventListener?.("change", onChange);
      // Запрос строится вокруг ТЕКУЩЕГО значения и пересоздаётся после каждой
      // смены: у matchMedia нет запроса «любое изменение DPR».
      mql = window.matchMedia(`(resolution: ${current}dppx)`);
      mql.addEventListener?.("change", onChange);
    };
    const onChange = () => subscribe();
    subscribe();
    return () => {
      cancelled = true;
      mql?.removeEventListener?.("change", onChange);
    };
  }, []);
  return dpr;
}
// Один и тот же mxc:// идентификатор может существовать на разных homeserver.
// Поэтому кэш и незавершённые запросы изолированы по MatrixClient/сессии.
const avatarCaches = new WeakMap<MatrixClient, Map<string, string>>();
const avatarInFlights = new WeakMap<MatrixClient, Map<string, Promise<string | null>>>();

function avatarCacheFor(client: MatrixClient): Map<string, string> {
  let cache = avatarCaches.get(client);
  if (!cache) {
    cache = new Map<string, string>();
    avatarCaches.set(client, cache);
  }
  return cache;
}

function avatarInFlightFor(client: MatrixClient): Map<string, Promise<string | null>> {
  let inFlight = avatarInFlights.get(client);
  if (!inFlight) {
    inFlight = new Map<string, Promise<string | null>>();
    avatarInFlights.set(client, inFlight);
  }
  return inFlight;
}

/**
 * Ключ кэша ВКЛЮЧАЕТ запрошенный размер (п.9). Без него рельс (40px) и
 * строка списка (44px) делили бы одну запись, и тот, кто пришёл вторым,
 * получал чужой размер — именно так и появляется «размыто только иногда».
 * `size = 0` — оригинал (старое поведение для вызовов без displaySize).
 */
function avatarKey(mxcUrl: string, size: number): string {
  return size > 0 ? `${mxcUrl}|${size}` : mxcUrl;
}

async function loadAvatar(
  client: MatrixClient,
  mxcUrl: string,
  thumbPx = 0,
): Promise<string | null> {
  const avatarCache = avatarCacheFor(client);
  const avatarInFlight = avatarInFlightFor(client);
  const key = avatarKey(mxcUrl, thumbPx);
  const cached = avatarCache.get(key);
  if (cached) return cached;

  const running = avatarInFlight.get(key);
  if (running) return running;

  const task = (async () => {
    try {
      let blob: Blob | null = null;
      if (thumbPx > 0) {
        // СЕРВЕРНОЕ ПРЕВЬЮ нужного размера, method=crop — аватар квадратный
        // и обрезается по кругу; scale оставил бы поля и фактически уменьшил
        // полезную площадь картинки.
        try {
          const httpUrl = client.mxcUrlToHttp(
            mxcUrl, thumbPx, thumbPx, "crop", false, true, true,
          );
          if (httpUrl) {
            const res = await fetch(httpUrl, {
              headers: { Authorization: `Bearer ${client.getAccessToken() ?? ""}` },
            });
            if (res.ok) blob = await res.blob();
          }
        } catch {
          // Сетевая ошибка превью — ниже пробуем оригинал.
        }
      }
      // ЗАПАСНОЙ ПУТЬ ОБЯЗАТЕЛЕН: Synapse не делает thumbnail для части
      // форматов (например SVG) и отвечает ошибкой. Без отката на оригинал
      // мы бы потеряли аватары, которые раньше работали — регрессия хуже
      // исходной размытости.
      if (!blob) blob = await fetchAuthedBlob(client, mxcUrl);
      const objectUrl = URL.createObjectURL(blob);
      // Вытеснение самого старого: Map хранит порядок вставки.
      if (avatarCache.size >= AVATAR_CACHE_LIMIT) {
        const oldest = avatarCache.keys().next().value;
        if (oldest) {
          const dead = avatarCache.get(oldest);
          if (dead) URL.revokeObjectURL(dead);
          avatarCache.delete(oldest);
        }
      }
      avatarCache.set(key, objectUrl);
      return objectUrl;
    } catch {
      // Аватар не загрузился — это не ошибка экрана: рисуем инициалы.
      return null;
    } finally {
      avatarInFlight.delete(key);
    }
  })();

  avatarInFlight.set(key, task);
  return task;
}

/**
 * blob:-URL аватара по mxc-адресу, или null (нет аватара / не загрузился).
 * На null вызывающий код рисует инициалы — как и раньше.
 */
export function useAuthedAvatar(
  client: MatrixClient,
  mxcUrl: string | null,
  /**
   * ОТОБРАЖАЕМЫЙ размер аватара в CSS-пикселях (Антон 2026-09-18, п.9).
   * Передан — тянем серверное превью ≥2× с учётом DPR и method=crop.
   * Не передан — прежнее поведение (оригинал), чтобы ни один старый
   * вызов не изменил поведения молча.
   */
  displaySize?: number,
): string | null {
  const dpr = useDevicePixelRatio();
  // dpr участвует в расчёте внутри avatarThumbPx; держим его в зависимостях
  // явно, иначе переезд окна на Retina не перезапросит картинку.
  const thumbPx = displaySize && displaySize > 0 ? avatarThumbPx(displaySize) : 0;
  void dpr;
  const key = mxcUrl ? avatarKey(mxcUrl, thumbPx) : null;

  // Стартуем сразу с кэша: иначе уже загруженный аватар на один кадр
  // мигал бы инициалами при каждом перерисовывании списка.
  const [url, setUrl] = useState<string | null>(() => (
    key ? avatarCacheFor(client).get(key) ?? null : null
  ));

  useEffect(() => {
    if (!mxcUrl || !key) {
      setUrl(null);
      return;
    }
    const cached = avatarCacheFor(client).get(key);
    if (cached) {
      setUrl(cached);
      return;
    }
    // Не показываем аватар предыдущей комнаты/сессии, пока новый запрос идёт
    // или если он завершится ошибкой.
    setUrl(null);
    let cancelled = false;
    void loadAvatar(client, mxcUrl, thumbPx).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [client, mxcUrl, key, thumbPx]);

  return url;
}

/**
 * То же, что useAuthedMedia, но отдаёт ещё и сам Blob.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ХУК: лайтбоксу Blob нужен дважды из одной загрузки —
 * показать (blob:-URL) и отдать наружу («Скачать» строит <a download> из
 * того же URL, а «Поделиться» через Web Share API требует настоящий File,
 * собранный из Blob). Качать второй раз ради кнопки — двойной трафик на
 * каждый просмотр, а на большом видео это заметно.
 *
 * Blob живёт в состоянии и освобождается вместе с URL при размонтировании
 * (URL.revokeObjectURL; сам Blob сборщик мусора заберёт сам).
 */
export function useAuthedMediaBlob(
  client: MatrixClient,
  mxcUrl: string | null,
): { url: string | null; blob: Blob | null; error: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mxcUrl) {
      setUrl(null);
      setBlob(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const b = await fetchAuthedBlob(client, mxcUrl);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(b);
        setUrl(objectUrl);
        setBlob(b);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, mxcUrl]);

  return { url, blob, error };
}
